// Copyright 2025 Mo-Sys Engineering Ltd. All Rights Reserved.
import { createAction } from '@reduxjs/toolkit';

/**
 * ================================
 * WebSocket  Actions
 * ================================
 * Called and executed by the WebSocket middleware.
 */

/**
 * Open a WebSocket connection
 * ================================
 * Called at the top level of your application after user authentication to establish the WebSocket connection to server.
 * Usage:
 * dispatch(wsConnect({ token, url }))
 * ================================
 */
export const wsConnect = createAction('ws/connect');

/**
 *  Close the WebSocket connection manually to clear connection on unmount or logout
 */
export const wsDisconnect = createAction('ws/disconnect');

/**
 * Send a raw WebSocket message.
 *
 * IMPORTANT:
 * - Payload MUST be a plain JS object
 * - Middleware will JSON.stringify it before sending
 *
 * Prefer using the helper action creators below instead of calling wsSend directly.
 */
export const wsSend = createAction('ws/send');

/**
 * Subscribe to specific websocket events.
 * ================================
 * Usage:
 * dispatch(wsRegister({ event: "ModelDataChanged" }));
 * dispatch(wsRegister({ data: { studioUUID: "studio-123" } }));
 * ===============================
 */
export const wsRegister = ({ event, data }) =>
  wsSend({
    type: 'register',
    body: {
      body: {
        event,
        data,
      },
    },
  });

/**
 * Unsubscribe to specific websocket events.
 * ================================
 * Usage:
 * dispatch(wsDeregister({ data: { studioUUID: "studio-123" } }));
 * ================================
 */
export const wsDeregister = ({ event, data }) =>
  wsSend({
    type: 'deregister',
    body: {
      body: {
        event,
        data,
      },
    },
  });

/**
 * Desired state for scene in a studio.
 * ================================
 * Usage:
 * dispatch(
 *   wsSceneInStudioDesiredStateChanged({
 *     studioUUID: "studio-123",
 *     sceneUUID: "scene-456",
 *     type: "2D",  possible values: "2D", "2.5D", "Scan","3D"
 *     state: "downloaded",  possible values: "downloaded", "undownloaded", "loaded"
 *   })
 * );
 * ================================
 */
export const wsSceneInStudioDesiredStateChanged = ({ studioUUID, sceneUUID, type, state }) =>
  wsSend({
    type: 'event',
    body: {
      event: 'SceneInStudioDesiredStateChanged',
      data: {
        studioUUID,
        sceneUUID,
        type,
        state,
      },
    },
  });

/**
 * Action for individual pieces of equipment.
 * ================================
 * Usage:
 * dispatch(
 *   wsStudioEquipmentAction({
 *     studioUUID: "studio-123",
 *     equipmentUUID: "equip-456",
 *     deviceId: "device-789", // optional
 *     action: "identify",
 *   })
 * );
 * ================================
 */
export const wsStudioEquipmentAction = ({ studioUUID, equipmentUUID, deviceId = '', action }) =>
  wsSend({
    type: 'event',
    body: {
      event: 'StudioEquipmentAction',
      data: {
        studioUUID,
        equipmentUUID,
        device_id: deviceId,
        action,
      },
    },
  });

/**
 * Action for group of equipment.
 * ================================
 * Usage:
 * dispatch(
 *   wsStudioGroupAction({
 *     studioUUID: "studio-123",
 *     equipmentUUID: "equip-456(ex.Camera 1)",
 *     deviceId: "device-789", // optional
 *     action: "GoToPosition",
 *   })
 * );
 * ================================
 */
export const wsStudioGroupAction = ({ studioUUID, equipmentUUID, deviceId = '', action }) =>
  wsSend({
    type: 'event',
    body: {
      event: 'StudioGroupAction',
      data: {
        studioUUID,
        equipmentUUID,
        device_id: deviceId,
        action,
      },
    },
  });

/**
 * Desired state for individual pieces of equipment (Settings/Properties).
 * ================================
 * Usage:
 * dispatch(
 *   wsStudioEquipmentDesiredState({
 *     studioUUID: "studio-123",
 *     equipmentUUID: "equip-456",
 *     deviceId: "device-789", // optional
 *     settings: { enabled: true },
 *   })
 * );
 * ================================
 */
export const wsStudioEquipmentDesiredState = ({
  studioUUID,
  equipmentUUID,
  deviceId = '',
  settings = {},
}) =>
  wsSend({
    type: 'event',
    body: {
      event: 'EquipmentInStudioDesiredStateChanged',
      data: {
        studioUUID,
        equipmentUUID,
        device_id: deviceId,
        settings,
      },
    },
  });
